import { IDrawingButton } from "../Forms/Drawing/DrawingButton";
import { FormInput } from "../Forms/formFields";
import { Condition, Input } from "./enums";

export interface IConfig {
    default: IDefault;
    basemapLVM: IBasemapLVM;
    basemapOrto: IBasemapOrto;
    layers: ILayerConfig[];
    geoLocation?: ILocation;
    forms: IForms;
    search: ISearch;
    reCaptchaSiteKey: string;
    upload: IUploadConfig;
    print: IPrintConfig;
    email: IEmailConfig;
}
export interface IDefault {
    center: number[];
    extentSize: number[];
    zoom: number;
    minZoom: number;
    maxZoom?: number;
    projection: string;
    zoomToLevel: number;
    clusterLayerId: string;
    polygonLayerId: string;
    drawingLayerId: string;
    searchResultLayerId: string;
    clusterDistance: number;
    polygonZoomThreshold: number;
}

export interface IBasemapLVM {
    tileGrid: {
        extent: number[];
        origin: number[];
        tileSize: number[];
        resolutions: number[];
    };
    source: {
        url: string;
        maxZoom: number;
        projection: string;
        crossOrigin: string;
    };
}
export interface IBasemapOrto {
    source: {
        url: string;
        params: {
            LAYERS: string;
            FORMAT: string;
            transparent: boolean;
        };
        projection: string;
        serverType: string;
        crossOrigin: string;
    };
}
export interface ILayerConfig {
    id: string;
    title: string;
    copyright: string;
    url: string;
    crossOrigin: string;
    visible?: boolean;
}
export interface ILocation {
    geolocationOptions?: {
        maximumAge: number;
        timeout: number;
        enableHighAccuracy: boolean;
    };
}

export interface IIdentify {
    fields: string[];
    conditionalFields: IIdentifyCondition[];
}

export interface IIdentifyCondition {
    field: string;
    value: string | number | boolean;
    condition: Condition;
    showFields: string[];
}

export interface IForms {
    textFilter: IInputFieldConfig;
    individualApplicantForm: IFormConfig;
    companyApplicantForm: IFormConfig;
    termForm: IFormConfig;
}

export interface IFormConfig {
    fields: IInputFieldConfig[];
}
export interface IInputFieldConfig {
    field: string;
    title: string;
    type?: Input;
    autocomplete?: string;
    required?: boolean;
    validationFn?: (value) => boolean;
    validationErrorMessage?: string;
    visibilityDeterminantField?: string;
    visibilityDeterminantFieldVisibleValue?: string | boolean | number;
    info?: string;
    minDateField?: string;
    maxDateField?: string;
    minDate?: string;
    maxDate?: string;
    hasError?: boolean;
    comment?: string;
    maxlength?: number; // max length for Text or TextArea fields
}

export interface ISearch {
    url: string;
    searchLayers: ISearchLayer[];
}

export interface ISearchLayer {
    layerId: string;
    field: string;
    displayFieldName?: string;
}

export interface IUploadConfig {
    url: string;
}

export interface IPrintConfig {
    endpoint: string;
    url: string;
}

export interface IEmailConfig {
    subject: string;
    body: string;
    url: string;
}

export const proxyUrl = process.env.NODE_ENV === "development" ? "https://conexustestmap.lvm.lv/" : "";
export const all = "visi";
export const defaultOption = { name: all, code: undefined };

export const config: IConfig = {
    default: {
        center: [530000, 310000],
        extentSize: [530000, 310000],
        zoom: 4.5,
        minZoom: 3.5,
        maxZoom: 20,
        projection: "EPSG:3059",
        zoomToLevel: 13,
        clusterLayerId: "clusterLayer",
        polygonLayerId: "polygonLayer",
        drawingLayerId: "drawingLayer",
        searchResultLayerId: "searchResultLayer",
        clusterDistance: 80,
        polygonZoomThreshold: 11.44444444
    },
    basemapLVM: {
        tileGrid: {
            extent: [-1349424.723447654, -595003.4846995564, 2424864.0751299444, 1206812.6189326514],
            origin: [-5120900, 3998100],
            tileSize: [512, 512],
            resolutions: [
                1058.3354500042335,
                529.1677250021168,
                264.5838625010584,
                132.2919312505292,
                52.91677250021167,
                26.458386250105836,
                13.229193125052918,
                10.583354500042335,
                7.9375158750317505,
                5.291677250021167,
                3.9687579375158752,
                2.6458386250105836,
                1.3229193125052918,
                0.5291677250021167
            ]
        },
        source: {
            url: proxyUrl + "proxy/D341478CE74F4F02B68607991448D499/CloudMap_mammadaba/MapServer/tile/{z}/{y}/{x}",
            maxZoom: 14,
            projection: "EPSG:3059",
            crossOrigin: "anonymous"
        }
    },
    basemapOrto: {
        source: {
            url: "https://lvmgeoserver.lvm.lv/geoserver/ows",
            params: {
                LAYERS: "public:Orto_LKS",
                FORMAT: "image/vnd.jpeg-png8",
                transparent: false
            },
            projection: "EPSG:3857",
            serverType: "geoserver",
            crossOrigin: "anonymous"
        }
    },
    layers: [
        {
            id: "cadaster",
            title: "Kadastra karte",
            copyright: "Valsts Zemes dienests",
            url: proxyUrl + "proxy/7cb29dcbb61442b7a22c289b22d671b8/MapServer",
            crossOrigin: "anonymous"
        }
        // {
        //     id: "safetyZones",
        //     title: "Aizsargjoslas",
        //     copyright: "Conexus",
        //     url: proxyUrl + "proxy/f7b4cfcd59fc471da3ad2013972c26cc/MapServer",
        //     crossOrigin: "anonymous",
        //     visible: true
        // }
    ],
    geoLocation: {
        geolocationOptions: {
            maximumAge: 0,
            timeout: 15000,
            enableHighAccuracy: true
        }
    },
    forms: {
        textFilter: {
            field: "TEXT-FILTER",
            title: "Teksta meklēšana"
        },
        companyApplicantForm: {
            fields: [
                {
                    field: "APPLICANTNAME",
                    title: "Uzņēmuma nosaukums",
                    type: Input.Text,
                    autocomplete: "organization",
                    required: true,
                    maxlength: 100,
                    validationFn: (value): boolean => value.length <= 100,
                    validationErrorMessage: "Pārsniedz 100 zīmes"
                },
                {
                    field: "APPLICANTCODE",
                    title: "Reģistrācijas numurs",
                    type: Input.Text,
                    required: true,
                    maxlength: 20,
                    validationFn: (value): boolean => value.length === 9 || value.length === 11,
                    validationErrorMessage: "Kļūdains reģistrācijas numurs"
                },
                {
                    field: "APPLICANTADDRESS",
                    title: "Adrese",
                    type: Input.Text,
                    required: true,
                    autocomplete: "street-address",
                    maxlength: 250,
                    validationFn: (value): boolean => value.length <= 250,
                    validationErrorMessage: "Pārsniedz 250 zīmes"
                },
                {
                    field: "APPLICANTPHONE",
                    title: "Tālrunis",
                    type: Input.Phone,
                    autocomplete: "tel",
                    required: true,
                    maxlength: 20,
                    validationFn: (value): boolean => (value.match(/^\d{8,11}$/) ? true : false), // only digits, 8-11 symbols
                    validationErrorMessage: "Kļūdains telefona numurs"
                },
                {
                    field: "APPLICANTEMAIL",
                    title: "E-pasts",
                    type: Input.Email,
                    autocomplete: "email",
                    required: true,
                    maxlength: 50,
                    validationFn: (value): boolean =>
                        value.match(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/) ? true : false,
                    validationErrorMessage: "Kļūdains epasts"
                },
                { field: "", title: "Paraksttiesīgā vai pilnvarotā persona:", type: Input.Label },
                {
                    field: "AUTHORIZEDPERSON",
                    title: "Vārds, uzvārds",
                    type: Input.Text,
                    required: true,
                    maxlength: 100,
                    validationFn: (value): boolean => value.length <= 100,
                    validationErrorMessage: "Pārsniedz 100 zīmes"
                },
                {
                    field: "POSITION",
                    title: "Ieņemamais amats",
                    type: Input.Text,
                    required: true,
                    maxlength: 150,
                    validationFn: (value): boolean => value.length <= 150,
                    validationErrorMessage: "Pārsniedz 150 zīmes"
                },
                {
                    field: "AUTHORIZEDPHONE",
                    title: "Tālrunis",
                    type: Input.Phone,
                    autocomplete: "tel",
                    required: true,
                    maxlength: 20,
                    validationFn: (value): boolean => value.length <= 20,
                    validationErrorMessage: "Pārsniedz 20 zīmes"
                },
                {
                    field: "AUTHORIZEDMAIL",
                    title: "E-pasts",
                    type: Input.Email,
                    autocomplete: "email",
                    required: true,
                    maxlength: 50,
                    validationFn: (value): boolean =>
                        value.match(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/) ? true : false,
                    validationErrorMessage: "Kļūdains epasts"
                }
            ]
        },
        individualApplicantForm: {
            fields: [
                {
                    field: "APPLICANTNAME",
                    title: "Vārds, uzvārds",
                    type: Input.Text,
                    required: true,
                    maxlength: 100,
                    validationFn: (value): boolean => value.length <= 100,
                    validationErrorMessage: "Pārsniedz 100 zīmes"
                },
                {
                    field: "APPLICANTADDRESS",
                    title: "Adrese",
                    type: Input.Text,
                    autocomplete: "street-address",
                    required: true,
                    maxlength: 290,
                    validationFn: (value): boolean => value.length <= 290,
                    validationErrorMessage: "Pārsniedz 290 zīmes"
                },
                {
                    field: "APPLICANTPHONE",
                    title: "Tālrunis",
                    type: Input.Phone,
                    autocomplete: "tel",
                    required: true,
                    maxlength: 20,
                    validationFn: (value): boolean => (value.match(/^\d{8,11}$/) ? true : false), // only digits, 8-11 symbols
                    validationErrorMessage: "Kļūdains telefona numurs"
                },
                {
                    field: "APPLICANTEMAIL",
                    title: "E-pasts",
                    type: Input.Email,
                    autocomplete: "email",
                    required: true,
                    maxlength: 50,
                    validationFn: (value): boolean =>
                        value.match(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/) ? true : false,
                    validationErrorMessage: "Kļūdains epasts"
                },
                { field: "OTHERCONTACTPERSON", title: "Cita kontaktpersona", type: Input.Checkbox },
                {
                    field: "CONTACTPERSON",
                    title: "Kontaktpersona",
                    type: Input.Text,
                    required: true,
                    visibilityDeterminantField: "OTHERCONTACTPERSON",
                    visibilityDeterminantFieldVisibleValue: true,
                    maxlength: 100,
                    validationFn: (value): boolean => value.length <= 100,
                    validationErrorMessage: "Pārsniedz 100 zīmes"
                },
                {
                    field: "CONTACTPERSONPHONE",
                    title: "Kontaktpersonas tālrunis",
                    type: Input.Phone,
                    autocomplete: "tel",
                    required: true,
                    visibilityDeterminantField: "OTHERCONTACTPERSON",
                    visibilityDeterminantFieldVisibleValue: true,
                    maxlength: 20,
                    validationFn: (value): boolean => value.length <= 20,
                    validationErrorMessage: "Pārsniedz 20 zīmes"
                }
            ]
        },
        termForm: {
            fields: [
                {
                    field: "OBJECTNAME",
                    title: "Objekta nosaukums",
                    type: Input.Text,
                    required: true,
                    maxlength: 150,
                    validationFn: (value): boolean => value.length <= 150,
                    validationErrorMessage: "Pārsniedz 150 zīmes"
                },
                {
                    field: "DATEFROM",
                    title: "Darbības termiņš no",
                    type: Input.Date,
                    required: true,
                    maxDateField: "DATETO"
                },
                {
                    field: "DATETO",
                    title: "Darbības termiņš līdz",
                    type: Input.Date,
                    required: true,
                    minDateField: "DATEFROM"
                },
                {
                    field: "TECHNICALNUMBER",
                    title: "Tehnisko noteikumu numurs",
                    type: Input.Text,
                    maxlength: 10,
                    validationFn: (value): boolean => value.length <= 10,
                    validationErrorMessage: "Atļautas 10 zīmes"
                },
                {
                    field: "TECHNICALDATE",
                    title: "Tehnisko noteikumu datums",
                    type: Input.Date,
                    maxlength: 20,
                    validationErrorMessage: "Atļautas 20 zīmes"
                },
                {
                    field: "PROJECTNUMBER",
                    title: "Projekta saskaņošanas numurs",
                    type: Input.Text,
                    maxlength: 10,
                    validationFn: (value): boolean => value.length <= 10,
                    validationErrorMessage: "Atļautas 10 zīmes"
                },
                {
                    field: "PROJECTDATE",
                    title: "Projekta saskaņošanas datums",
                    type: Input.Date
                },
                { field: "", title: "Atbildīgais darbu vadītājs:", type: Input.Label },
                {
                    field: "RESPONSIBLEPERSON",
                    title: "Vārds, uzvārds",
                    type: Input.Text,
                    required: true,
                    maxlength: 100,
                    validationFn: (value): boolean => value.length <= 100,
                    validationErrorMessage: "Atļautas 100 zīmes"
                },
                {
                    field: "RESPONSIBLEPHONE",
                    title: "Tālrunis",
                    type: Input.Phone,
                    autocomplete: "tel",
                    required: true,
                    maxlength: 20,
                    validationFn: (value): boolean => value.length <= 20,
                    validationErrorMessage: "Atļautas 20 zīmes"
                },
                {
                    field: "RESPONSIBLEMAIL",
                    title: "E-pasts",
                    type: Input.Email,
                    autocomplete: "email",
                    required: true,
                    maxlength: 50,
                    validationFn: (value): boolean =>
                        value.match(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/) ? true : false,
                    validationErrorMessage: "Kļūdains epasts"
                }
            ]
        }
    },
    search: {
        url: proxyUrl + "proxy/03f9f34ad1244db2a6c951f604c539f8/MapServer",
        searchLayers: [{ layerId: "8", field: "CODE", displayFieldName: "CODE" }]
    },
    reCaptchaSiteKey: "6Le1IfMfAAAAAMGuZLBpB2_N6lk7uFfXeFI_7HHA",
    upload: {
        url: proxyUrl + "proxy/fp_save_67312ef2b5244f28a6183e2397755e88/api/Save"
    },
    print: {
        endpoint: "/print",
        url: proxyUrl + "proxy/print_846d8f58c3da4f91bc914f7fca62d0ec"
    },
    email: {
        subject: "Plānoto darbu pieteikums",
        body: "AS Conexus Baltic Grid” Jūsu iesniegtais plānoto darbu pieteikums ir saņemts.",
        url: proxyUrl + "proxy/email_4f7ad5fa1c62484cab58d7d64013f4aa/api/send"
    }
};
